import React, { useEffect, useState, useMemo } from "react";
import { useMsal } from "@azure/msal-react";
import DataTable, { TableColumn } from "react-data-table-component";
import {
    Building2,
    AlertTriangle,
    Users,
    Download,
    Clock,
    Calendar,
    Star,
    Award,
    FileSpreadsheet,
} from "lucide-react";
import * as XLSX from "xlsx";
import { Bar, Line } from "react-chartjs-2";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    PointElement,
    LineElement,
    Filler,
    Title,
    Tooltip,
    Legend,
} from "chart.js";
import { getApiBaseUrl } from "../utils/baseUrl";
import { useLoading, acquireTokenSafe } from "../App";
import DataSourcePanel from "./DataSourcePanel";

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    PointElement,
    LineElement,
    Filler,
    Title,
    Tooltip,
    Legend,
);

interface TenureClient {
    ClientID: string;
    Title: string;
    ClientRef: string;
    ClientState: string;
    ClientStateRaw: string;
    ClientType: string;
    ManagerName: string;
    IsGoldPartner: boolean;
    IsPartner: boolean;
    CreatedDate: string;
    tenureDays: number;
    tenureYears: number;
    tenureMonths: number;
}

const TENURE_CATEGORIES = [
    "< 6 months",
    "6-12 months",
    "1-2 years",
    "2-3 years",
    "3-5 years",
    "5+ years",
] as const;
type TenureCategory = (typeof TENURE_CATEGORIES)[number];

const tenureCategoryFor = (days: number): TenureCategory => {
    const years = days / 365.25;
    if (years < 0.5) return "< 6 months";
    if (years < 1) return "6-12 months";
    if (years < 2) return "1-2 years";
    if (years < 3) return "2-3 years";
    if (years < 5) return "3-5 years";
    return "5+ years";
};

const tenureCategoryClass = (cat: TenureCategory): string => {
    switch (cat) {
        case "5+ years":
            return "renewable";
        case "3-5 years":
            return "moderate";
        case "2-3 years":
            return "moderate";
        case "1-2 years":
            return "at-risk";
        default:
            return "one-off";
    }
};

const formatTenure = (days: number): string => {
    const years = Math.floor(days / 365.25);
    const remDays = days - Math.floor(years * 365.25);
    const months = Math.floor(remDays / 30.44);
    if (years === 0 && months === 0) return `${days}d`;
    if (years === 0) return `${months}mo`;
    return `${years}y ${months}mo`;
};

const ClientTenureReport: React.FC = () => {
    const { instance, accounts } = useMsal();
    const { setLoading } = useLoading();
    const [clients, setClients] = useState<TenureClient[]>([]);
    const [search, setSearch] = useState("");
    const [activeOnly, setActiveOnly] = useState(true);
    const [typeFilter, setTypeFilter] = useState<string>("all");
    const [tenureFilter, setTenureFilter] = useState<string>("all");
    const [partnerFilter, setPartnerFilter] = useState<string>("all");
    const [error, setError] = useState<string | null>(null);
    const [lastSentaPull, setLastSentaPull] = useState<string | null>(null);

    useEffect(() => {
        const fetchData = async () => {
            if (accounts.length === 0) return;
            setLoading(true);
            try {
                const token = await acquireTokenSafe(instance, accounts);
                if (!token) {
                    setLoading(false);
                    return;
                }
                const res = await fetch(`${getApiBaseUrl()}/api/client-tenure`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const data = await res.json();
                setClients(data.clients || []);
                setLastSentaPull(data.lastSentaPull || null);
                setError(null);
            } catch (err: any) {
                setError(err.message || "Failed to load");
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [accounts, instance, setLoading]);

    // Filter list options
    const clientTypes = useMemo(() => {
        const set = new Set<string>();
        clients.forEach(c => {
            if (c.ClientType) set.add(c.ClientType);
        });
        return Array.from(set).sort();
    }, [clients]);

    // Apply filters
    const filteredClients = useMemo(() => {
        let data = clients;

        if (activeOnly) {
            // Active = Client state (l-clientstate-c)
            data = data.filter(c => c.ClientStateRaw === "l-clientstate-c");
        }

        if (partnerFilter === "gold") data = data.filter(c => c.IsGoldPartner);
        else if (partnerFilter === "partner")
            data = data.filter(c => c.IsPartner && !c.IsGoldPartner);
        else if (partnerFilter === "client")
            data = data.filter(c => !c.IsPartner && !c.IsGoldPartner);

        if (typeFilter !== "all")
            data = data.filter(c => c.ClientType === typeFilter);

        if (tenureFilter !== "all")
            data = data.filter(c => tenureCategoryFor(c.tenureDays) === tenureFilter);

        if (search) {
            const s = search.toLowerCase();
            data = data.filter(
                c =>
                    c.Title.toLowerCase().includes(s) ||
                    (c.ClientRef || "").toLowerCase().includes(s) ||
                    (c.ManagerName || "").toLowerCase().includes(s),
            );
        }

        return data;
    }, [clients, search, activeOnly, partnerFilter, typeFilter, tenureFilter]);

    // Stats
    const stats = useMemo(() => {
        const totalDays = filteredClients.reduce((s, c) => s + c.tenureDays, 0);
        const avgYears =
            filteredClients.length > 0
                ? totalDays / filteredClients.length / 365.25
                : 0;
        const fiveYearPlus = filteredClients.filter(c => c.tenureDays / 365.25 >= 5)
            .length;
        const newClients = filteredClients.filter(c => c.tenureDays / 365.25 < 1)
            .length;
        return {
            count: filteredClients.length,
            avgYears,
            fiveYearPlus,
            newClients,
        };
    }, [filteredClients]);

    // Tenure distribution counts (for filter dropdown labels)
    const tenureCounts = useMemo(() => {
        const map = new Map<TenureCategory, number>();
        TENURE_CATEGORIES.forEach(c => map.set(c, 0));
        filteredClients.forEach(c => {
            const cat = tenureCategoryFor(c.tenureDays);
            map.set(cat, (map.get(cat) || 0) + 1);
        });
        return map;
    }, [filteredClients]);

    // Tenure-bracket × partner-tier breakdown for the stacked bar chart
    const tenureBreakdown = useMemo(() => {
        const tiers = ["gold", "partner", "client"] as const;
        const buckets: Record<string, Record<string, number>> = {};
        TENURE_CATEGORIES.forEach(c => {
            buckets[c] = { gold: 0, partner: 0, client: 0 };
        });
        filteredClients.forEach(c => {
            const cat = tenureCategoryFor(c.tenureDays);
            const tier: typeof tiers[number] = c.IsGoldPartner
                ? "gold"
                : c.IsPartner
                ? "partner"
                : "client";
            buckets[cat][tier] += 1;
        });
        return buckets;
    }, [filteredClients]);

    // Clients-acquired-by-year line chart
    const acquisitionByYear = useMemo(() => {
        const map = new Map<number, number>();
        filteredClients.forEach(c => {
            if (!c.CreatedDate) return;
            const y = new Date(c.CreatedDate).getFullYear();
            if (!Number.isFinite(y)) return;
            map.set(y, (map.get(y) || 0) + 1);
        });
        const years = Array.from(map.keys()).sort((a, b) => a - b);
        if (years.length === 0) return { labels: [] as string[], counts: [] as number[] };
        const min = years[0];
        const max = years[years.length - 1];
        const labels: string[] = [];
        const counts: number[] = [];
        for (let y = min; y <= max; y++) {
            labels.push(String(y));
            counts.push(map.get(y) || 0);
        }
        return { labels, counts };
    }, [filteredClients]);

    const downloadCSV = () => {
        const headers = [
            "Client Ref",
            "Client",
            "Type",
            "State",
            "Manager",
            "Date Created",
            "Tenure",
            "Tenure Days",
            "Category",
        ];
        const rows = filteredClients.map(c => [
            c.ClientRef,
            `"${(c.Title || "").replace(/"/g, '""')}"`,
            `"${(c.ClientType || "").replace(/"/g, '""')}"`,
            `"${(c.ClientState || "").replace(/"/g, '""')}"`,
            `"${(c.ManagerName || "").replace(/"/g, '""')}"`,
            new Date(c.CreatedDate).toISOString().slice(0, 10),
            formatTenure(c.tenureDays),
            c.tenureDays,
            tenureCategoryFor(c.tenureDays),
        ]);
        const csv = [headers.join(","), ...rows.map(r => r.join(","))].join("\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `client-tenure-${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const downloadXLSX = () => {
        const rows = filteredClients.map(c => ({
            "Client Ref": c.ClientRef,
            Client: c.Title,
            Type: c.ClientType,
            State: c.ClientState,
            Manager: c.ManagerName,
            "Date Created": new Date(c.CreatedDate).toISOString().slice(0, 10),
            Tenure: formatTenure(c.tenureDays),
            "Tenure Days": c.tenureDays,
            Category: tenureCategoryFor(c.tenureDays),
            "Gold Partner": c.IsGoldPartner ? "Yes" : "",
            Partner: c.IsPartner ? "Yes" : "",
        }));
        const ws = XLSX.utils.json_to_sheet(rows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Tenure");
        XLSX.writeFile(
            wb,
            `client-tenure-${new Date().toISOString().slice(0, 10)}.xlsx`,
        );
    };

    const columns: TableColumn<TenureClient>[] = [
        {
            name: "Ref",
            selector: row => row.ClientRef,
            sortable: true,
            width: "100px",
        },
        {
            name: "Client",
            selector: row => row.Title,
            sortable: true,
            grow: 2,
            cell: row => (
                <div>
                    <div style={{ fontWeight: 500 }}>{row.Title}</div>
                    {(row.IsGoldPartner || row.IsPartner) && (
                        <div style={{ marginTop: 2, display: "flex", gap: 4 }}>
                            {row.IsGoldPartner && (
                                <span
                                    style={{
                                        fontSize: "0.65rem",
                                        padding: "1px 6px",
                                        borderRadius: 3,
                                        background: "rgba(245, 158, 11, 0.18)",
                                        color: "#f59e0b",
                                        fontWeight: 600,
                                    }}
                                >
                                    GOLD
                                </span>
                            )}
                            {row.IsPartner && !row.IsGoldPartner && (
                                <span
                                    style={{
                                        fontSize: "0.65rem",
                                        padding: "1px 6px",
                                        borderRadius: 3,
                                        background: "rgba(59, 130, 246, 0.18)",
                                        color: "#3b82f6",
                                        fontWeight: 600,
                                    }}
                                >
                                    PARTNER
                                </span>
                            )}
                        </div>
                    )}
                </div>
            ),
        },
        {
            name: "Type",
            selector: row => row.ClientType,
            sortable: true,
            width: "200px",
            cell: row => (
                <span style={{ color: "var(--text-secondary)" }}>
                    {row.ClientType || "—"}
                </span>
            ),
        },
        {
            name: "State",
            selector: row => row.ClientState,
            sortable: true,
            width: "130px",
            cell: row => (
                <span style={{ color: "var(--text-secondary)" }}>
                    {row.ClientState || "—"}
                </span>
            ),
        },
        {
            name: "Manager",
            selector: row => row.ManagerName,
            sortable: true,
            width: "160px",
            cell: row => (
                <span style={{ color: "var(--text-secondary)" }}>
                    {row.ManagerName || "—"}
                </span>
            ),
        },
        {
            name: "Created",
            selector: row => row.CreatedDate,
            sortable: true,
            width: "110px",
            cell: row => (
                <span style={{ fontVariantNumeric: "tabular-nums" }}>
                    {new Date(row.CreatedDate).toISOString().slice(0, 10)}
                </span>
            ),
        },
        {
            name: "Tenure",
            selector: row => row.tenureDays,
            sortable: true,
            width: "110px",
            cell: row => (
                <span style={{ fontVariantNumeric: "tabular-nums" }}>
                    {formatTenure(row.tenureDays)}
                </span>
            ),
        },
        {
            name: "Bracket",
            selector: row => row.tenureDays,
            sortable: true,
            width: "120px",
            cell: row => {
                const cat = tenureCategoryFor(row.tenureDays);
                return (
                    <span className={`status-badge ${tenureCategoryClass(cat)}`}>
                        {cat}
                    </span>
                );
            },
        },
    ];

    if (error) {
        return (
            <div className="page-container">
                <div className="page-header">
                    <div className="page-title">
                        <Clock size={28} />
                        <h1>Client Tenure</h1>
                    </div>
                </div>
                <div className="warning-banner">
                    <AlertTriangle size={20} />
                    <span>Error loading tenure data: {error}</span>
                </div>
            </div>
        );
    }

    return (
        <div className="page-container">
            <div className="page-header compact">
                <div className="page-title">
                    <Clock size={20} />
                    <h1>Client Tenure</h1>
                </div>
            </div>

            <div className="stats-grid stats-grid-4">
                <div className="stat-card">
                    <div className="stat-icon">
                        <Users size={20} />
                    </div>
                    <div className="stat-content">
                        <div className="stat-value">{stats.count.toLocaleString()}</div>
                        <div className="stat-label">Clients in view</div>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon" style={{ color: "#10b981" }}>
                        <Calendar size={20} />
                    </div>
                    <div className="stat-content">
                        <div className="stat-value">{stats.avgYears.toFixed(1)}y</div>
                        <div className="stat-label">Average tenure</div>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon" style={{ color: "#f59e0b" }}>
                        <Star size={20} />
                    </div>
                    <div className="stat-content">
                        <div className="stat-value">{stats.fiveYearPlus.toLocaleString()}</div>
                        <div className="stat-label">5+ year clients</div>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon" style={{ color: "#3b82f6" }}>
                        <Award size={20} />
                    </div>
                    <div className="stat-content">
                        <div className="stat-value">{stats.newClients.toLocaleString()}</div>
                        <div className="stat-label">New ({"<"} 1 year)</div>
                    </div>
                </div>
            </div>

            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: "minmax(0, 1.4fr) minmax(0, 1fr)",
                    gap: 16,
                    marginBottom: 16,
                }}
            >
                <div
                    style={{
                        background: "var(--bg-secondary)",
                        border: "1px solid var(--border-color)",
                        borderRadius: 8,
                        padding: 16,
                    }}
                >
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            marginBottom: 12,
                        }}
                    >
                        <div
                            style={{
                                fontSize: "0.85rem",
                                fontWeight: 600,
                                color: "var(--text-primary)",
                                textTransform: "uppercase",
                                letterSpacing: 0.4,
                            }}
                        >
                            Tenure distribution by tier
                        </div>
                        <div style={{ fontSize: "0.75rem", color: "var(--text-secondary)" }}>
                            {filteredClients.length.toLocaleString()} clients
                        </div>
                    </div>
                    <div style={{ height: 260 }}>
                        <Bar
                            data={{
                                labels: TENURE_CATEGORIES as unknown as string[],
                                datasets: [
                                    {
                                        label: "Gold Partner",
                                        data: TENURE_CATEGORIES.map(c => tenureBreakdown[c].gold),
                                        backgroundColor: "#f59e0b",
                                        borderRadius: 4,
                                        stack: "tier",
                                    },
                                    {
                                        label: "Partner",
                                        data: TENURE_CATEGORIES.map(c => tenureBreakdown[c].partner),
                                        backgroundColor: "#3b82f6",
                                        borderRadius: 4,
                                        stack: "tier",
                                    },
                                    {
                                        label: "Client",
                                        data: TENURE_CATEGORIES.map(c => tenureBreakdown[c].client),
                                        backgroundColor: "#10b981",
                                        borderRadius: 4,
                                        stack: "tier",
                                    },
                                ],
                            }}
                            options={{
                                responsive: true,
                                maintainAspectRatio: false,
                                plugins: {
                                    legend: {
                                        position: "bottom" as const,
                                        labels: {
                                            color: "#e6edf3",
                                            usePointStyle: true,
                                            pointStyle: "circle",
                                            boxWidth: 8,
                                            padding: 14,
                                        },
                                    },
                                    tooltip: {
                                        backgroundColor: "rgba(13, 17, 23, 0.95)",
                                        titleColor: "#e6edf3",
                                        bodyColor: "#e6edf3",
                                        borderColor: "#30363d",
                                        borderWidth: 1,
                                        padding: 10,
                                    },
                                },
                                scales: {
                                    x: {
                                        stacked: true,
                                        ticks: { color: "#8b949e" },
                                        grid: { display: false },
                                    },
                                    y: {
                                        stacked: true,
                                        ticks: { color: "#8b949e", precision: 0 },
                                        grid: { color: "rgba(48, 54, 61, 0.5)" },
                                    },
                                },
                            }}
                        />
                    </div>
                </div>

                <div
                    style={{
                        background: "var(--bg-secondary)",
                        border: "1px solid var(--border-color)",
                        borderRadius: 8,
                        padding: 16,
                    }}
                >
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            marginBottom: 12,
                        }}
                    >
                        <div
                            style={{
                                fontSize: "0.85rem",
                                fontWeight: 600,
                                color: "var(--text-primary)",
                                textTransform: "uppercase",
                                letterSpacing: 0.4,
                            }}
                        >
                            Clients acquired per year
                        </div>
                        <div style={{ fontSize: "0.75rem", color: "var(--text-secondary)" }}>
                            {acquisitionByYear.labels.length > 0
                                ? `${acquisitionByYear.labels[0]} – ${acquisitionByYear.labels[acquisitionByYear.labels.length - 1]}`
                                : "—"}
                        </div>
                    </div>
                    <div style={{ height: 260 }}>
                        <Line
                            data={{
                                labels: acquisitionByYear.labels,
                                datasets: [
                                    {
                                        label: "New clients",
                                        data: acquisitionByYear.counts,
                                        borderColor: "#58a6ff",
                                        backgroundColor: "rgba(88, 166, 255, 0.18)",
                                        pointBackgroundColor: "#58a6ff",
                                        pointBorderColor: "#58a6ff",
                                        pointRadius: 3,
                                        pointHoverRadius: 5,
                                        tension: 0.35,
                                        fill: true,
                                        borderWidth: 2,
                                    },
                                ],
                            }}
                            options={{
                                responsive: true,
                                maintainAspectRatio: false,
                                plugins: {
                                    legend: { display: false },
                                    tooltip: {
                                        backgroundColor: "rgba(13, 17, 23, 0.95)",
                                        titleColor: "#e6edf3",
                                        bodyColor: "#e6edf3",
                                        borderColor: "#30363d",
                                        borderWidth: 1,
                                        padding: 10,
                                    },
                                },
                                scales: {
                                    x: {
                                        ticks: { color: "#8b949e" },
                                        grid: { display: false },
                                    },
                                    y: {
                                        ticks: { color: "#8b949e", precision: 0 },
                                        grid: { color: "rgba(48, 54, 61, 0.5)" },
                                        beginAtZero: true,
                                    },
                                },
                            }}
                        />
                    </div>
                </div>
            </div>

            <div className="table-controls">
                <input
                    type="text"
                    placeholder="Search clients..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    className="search-input"
                />
                <label
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        cursor: "pointer",
                        fontSize: "0.85rem",
                        color: "var(--text-secondary)",
                        whiteSpace: "nowrap",
                    }}
                >
                    <input
                        type="checkbox"
                        checked={activeOnly}
                        onChange={e => setActiveOnly(e.target.checked)}
                        style={{ accentColor: "var(--accent-blue)" }}
                    />
                    Active Only
                </label>
                <select
                    value={partnerFilter}
                    onChange={e => setPartnerFilter(e.target.value)}
                    className="filter-select"
                >
                    <option value="all">All Tiers</option>
                    <option value="gold">Gold Partners</option>
                    <option value="partner">Partners</option>
                    <option value="client">Clients Only</option>
                </select>
                <select
                    value={typeFilter}
                    onChange={e => setTypeFilter(e.target.value)}
                    className="filter-select"
                >
                    <option value="all">All Types</option>
                    {clientTypes.map(t => (
                        <option key={t} value={t}>
                            {t}
                        </option>
                    ))}
                </select>
                <select
                    value={tenureFilter}
                    onChange={e => setTenureFilter(e.target.value)}
                    className="filter-select"
                >
                    <option value="all">All Tenures</option>
                    {TENURE_CATEGORIES.map(c => (
                        <option key={c} value={c}>
                            {c} ({tenureCounts.get(c) || 0})
                        </option>
                    ))}
                </select>
                <button
                    onClick={downloadCSV}
                    className="download-btn"
                    title="Download as CSV"
                >
                    <Download size={16} />
                    Export CSV
                </button>
                <button
                    onClick={downloadXLSX}
                    className="download-btn"
                    title="Download as Excel"
                >
                    <FileSpreadsheet size={16} />
                    Export Excel
                </button>
            </div>

            <div className="data-table-container master-services-table">
                <DataTable
                    columns={columns}
                    data={filteredClients}
                    pagination
                    paginationPerPage={25}
                    paginationRowsPerPageOptions={[15, 25, 50, 100]}
                    highlightOnHover
                    responsive
                    defaultSortFieldId={7}
                    defaultSortAsc={false}
                    noDataComponent={
                        <div className="no-data">No clients match the current filters</div>
                    }
                    customStyles={{
                        table: {
                            style: {
                                backgroundColor: "var(--bg-secondary)",
                                borderRadius: "8px",
                            },
                        },
                        headRow: {
                            style: {
                                backgroundColor: "var(--bg-tertiary)",
                                borderBottomColor: "var(--border-color)",
                                color: "var(--text-primary)",
                                fontWeight: 600,
                                fontSize: "0.85rem",
                            },
                        },
                        headCells: {
                            style: {
                                color: "var(--text-secondary)",
                                paddingLeft: "16px",
                                paddingRight: "16px",
                            },
                        },
                        rows: {
                            style: {
                                backgroundColor: "var(--bg-secondary)",
                                color: "var(--text-primary)",
                                borderBottomColor: "var(--border-color)",
                                minHeight: "48px",
                            },
                            highlightOnHoverStyle: {
                                backgroundColor: "var(--bg-hover)",
                                color: "var(--text-primary)",
                                borderBottomColor: "var(--border-color)",
                            },
                        },
                        cells: {
                            style: {
                                paddingLeft: "16px",
                                paddingRight: "16px",
                            },
                        },
                        pagination: {
                            style: {
                                backgroundColor: "var(--bg-secondary)",
                                color: "var(--text-secondary)",
                                borderTopColor: "var(--border-color)",
                            },
                            pageButtonsStyle: {
                                color: "var(--text-secondary)",
                                fill: "var(--text-secondary)",
                                "&:hover:not(:disabled)": {
                                    backgroundColor: "var(--bg-hover)",
                                },
                                "&:disabled": {
                                    opacity: 0.4,
                                },
                            },
                        },
                        noData: {
                            style: {
                                backgroundColor: "var(--bg-secondary)",
                                color: "var(--text-secondary)",
                            },
                        },
                    }}
                />
            </div>

            <DataSourcePanel
                summary={
                    <>
                        Client tenure is computed from the cached Senta company snapshot in
                        the analytics warehouse — no live Senta REST call. Tenure is{" "}
                        <code>now() − senta_created_at</code>. Client state, type and
                        partner-tier flags are read from the same snapshot. The cache is
                        refreshed by the Senta sync job (see <em>Last sync</em> below).
                        {lastSentaPull && (
                            <>
                                {" "}
                                <strong>Last sync:</strong>{" "}
                                <code>{new Date(lastSentaPull).toLocaleString("en-IE")}</code>
                                .
                            </>
                        )}
                    </>
                }
                sections={[
                    {
                        title: "APIs called",
                        items: [
                            {
                                name: "GET /api/client-tenure",
                                description: (
                                    <>
                                        Postgres query against{" "}
                                        <code>senta_companies</code> (analytics warehouse, Heroku
                                        Postgres, eu-west-1). Server applies{" "}
                                        <code>clientStateLookup</code> and{" "}
                                        <code>clientTypeLookup</code> so the frontend gets human
                                        labels.
                                    </>
                                ),
                            },
                        ],
                    },
                    {
                        title: "Tables read",
                        items: [
                            {
                                name: "senta_companies",
                                description: (
                                    <>
                                        <code>senta_id</code>, <code>title</code>,{" "}
                                        <code>client_ref</code>, <code>client_state</code>,{" "}
                                        <code>client_type</code>,{" "}
                                        <code>manager_full_name</code>,{" "}
                                        <code>lead_staff_full_name</code>,{" "}
                                        <code>gold_partner</code>, <code>partner_client</code>,{" "}
                                        <code>senta_created_at</code> (used as the tenure start),{" "}
                                        <code>last_senta_pull</code>.
                                    </>
                                ),
                            },
                        ],
                    },
                    {
                        title: "Derivation",
                        items: [
                            {
                                name: "Tenure",
                                description: (
                                    <>
                                        <code>now() − senta_created_at</code>, returned as days
                                        and bucketed into &lt; 6mo, 6–12mo, 1–2y, 2–3y, 3–5y, 5+y.
                                    </>
                                ),
                            },
                            {
                                name: "Active Only filter",
                                description: (
                                    <>
                                        Restricts to{" "}
                                        <code>client_state = 'l-clientstate-c'</code> (i.e.{" "}
                                        <em>Client</em>). Disable to see prospects, former
                                        clients, onboarding, suspended, etc.
                                    </>
                                ),
                            },
                        ],
                    },
                ]}
            />
        </div>
    );
};

export default ClientTenureReport;
